import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

/** Lo que tarda en salir el tooltip con el ratón: sin espera, pasar por la fila llena la pantalla de globos. */
const ESPERA_MS = 250;

/**
 * Una acción de tabla: sólo el icono, con su nombre en un tooltip.
 *
 * - **El nombre siempre existe**: es el `aria-label` —lo que dice el lector de
 *   pantalla— y el texto del tooltip. Un icono sin nombre es un acertijo.
 * - **El tooltip sale al pasar el ratón y al llegar con el teclado**, no sólo
 *   con el ratón: quien navega con Tab también tiene que saber qué es.
 * - **Va en una capa aparte** (`document.body`), no dentro de la celda: las
 *   tablas viven en un contenedor con desplazamiento que recortaría un globo
 *   dibujado sólo con CSS en la primera fila y en el borde derecho.
 * - **El tono dice qué tan delicada es**: `peligro` para lo que quita o apaga,
 *   `principal` para la acción que la fila está esperando.
 */
export function AccionIcono({
  etiqueta,
  icono,
  onClick,
  to,
  tono = 'normal',
  disabled = false,
}: {
  etiqueta: string;
  icono: ReactNode;
  onClick?: () => void;
  /** Si es navegación y no una acción: un enlace de verdad, que se puede abrir en otra pestaña. */
  to?: string;
  tono?: 'normal' | 'peligro' | 'principal';
  disabled?: boolean;
}) {
  const { props, globo } = useTooltip(etiqueta);
  const clase = `accion-icono accion-icono--${tono}`;

  return (
    <>
      {to ? (
        <Link className={clase} to={to} aria-label={etiqueta} {...props}>
          {icono}
        </Link>
      ) : (
        <button className={clase} type="button" aria-label={etiqueta} disabled={disabled} onClick={onClick} {...props}>
          {icono}
        </button>
      )}
      {globo}
    </>
  );
}

/**
 * Lo mismo para algo que no es una acción pero se explica igual —un candado que
 * dice «protegido»—: se puede enfocar para leer el globo, y nada más.
 */
export function IconoConNota({ etiqueta, icono }: { etiqueta: string; icono: ReactNode }) {
  const { props, globo } = useTooltip(etiqueta);
  return (
    <>
      <span className="accion-icono accion-icono--nota" role="img" aria-label={etiqueta} tabIndex={0} {...props}>
        {icono}
      </span>
      {globo}
    </>
  );
}

function useTooltip(texto: string) {
  const ancla = useRef<HTMLElement | null>(null);
  const globoRef = useRef<HTMLSpanElement | null>(null);
  const espera = useRef<number | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(null);

  const mostrar = useCallback((conEspera: boolean) => {
    window.clearTimeout(espera.current);
    if (conEspera) espera.current = window.setTimeout(() => setVisible(true), ESPERA_MS);
    else setVisible(true);
  }, []);
  const ocultar = useCallback(() => {
    window.clearTimeout(espera.current);
    setVisible(false);
    setPosicion(null);
  }, []);

  // Arriba del icono y centrado; abajo si arriba no cabe, y siempre dentro de la
  // ventana. Se mide ya dibujado, antes de que se vea.
  useLayoutEffect(() => {
    if (!visible || !ancla.current || !globoRef.current) return;
    const caja = ancla.current.getBoundingClientRect();
    const globo = globoRef.current.getBoundingClientRect();
    const margen = 8;
    const arriba = caja.top - globo.height - 6;
    const top = arriba >= margen ? arriba : caja.bottom + 6;
    const centro = caja.left + caja.width / 2 - globo.width / 2;
    const left = Math.min(Math.max(centro, margen), window.innerWidth - globo.width - margen);
    setPosicion({ top, left });
  }, [visible]);

  // Si la página se mueve con el globo abierto, el globo se quedaría flotando
  // lejos de su icono: mejor que se vaya.
  useEffect(() => {
    if (!visible) return;
    window.addEventListener('scroll', ocultar, true);
    return () => window.removeEventListener('scroll', ocultar, true);
  }, [visible, ocultar]);

  useEffect(() => () => window.clearTimeout(espera.current), []);

  const props = {
    ref: (el: HTMLElement | null) => {
      ancla.current = el;
    },
    onMouseEnter: () => mostrar(true),
    onMouseLeave: ocultar,
    onFocus: () => mostrar(false),
    onBlur: ocultar,
  };

  const globo = visible
    ? createPortal(
        <span
          ref={globoRef}
          className="tooltip"
          aria-hidden="true"
          style={posicion ? { top: posicion.top, left: posicion.left } : { top: -9999, left: -9999 }}
        >
          {texto}
        </span>,
        document.body,
      )
    : null;

  return { props, globo };
}
